import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { ROOT_URL } from "../../api/ApiConfig";
import { mapApiCustomerToTableRow, applyApiCustomerCounts } from "../../utils/customerMappers";

const getAuthHeaders = (getState) => {
  const { loginToken } = getState();
  const authToken = loginToken?.response?.token;
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${authToken}`,
  };
};

const unwrap = (response) => response?.data;

function withoutCustomerTeamFields(body = {}) {
  const sanitized = { ...body };
  delete sanitized.team;
  delete sanitized.team_id;
  delete sanitized.teamId;
  delete sanitized.team_name;
  return sanitized;
}

function buildCustomersListKey(arg = {}) {
  const { page = 1, limit = 10, status, search, group_id } = arg;
  return [page, limit, status || "all", search || "", group_id || ""].join("::");
}

export const CUSTOMER_TAB_COUNT_STATUSES = [
  "all",
  "active",
  "waiting_reply",
  "inactive",
  "banned",
];

/**
 * Refetch totals for every customer tab (e.g. after ban/unban).
 * `search` must mirror the active query, otherwise these unfiltered totals
 * land after the list response and overwrite its search-aware count.
 */
export function refreshCustomerTabCounts(group_id, search) {
  return (dispatch) => {
    CUSTOMER_TAB_COUNT_STATUSES.forEach((status) => {
      dispatch(
        fetchCustomersList({
          page: 1,
          limit: 1,
          status,
          search: search || undefined,
          countsOnly: true,
          group_id,
        })
      );
    });
  };
}

export const fetchCustomersList = createAsyncThunk(
  "customers/fetchList",
  async (
    { page = 1, limit = 10, status, search, group_id, countsOnly } = {},
    { getState, rejectWithValue }
  ) => {
    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("limit", String(limit));
      if (status) params.set("status", status);
      if (search) params.set("search", search);
      if (group_id) params.set("group_id", String(group_id));

      const response = await axios.get(`${ROOT_URL}/customer/list?${params.toString()}`, {
        headers: getAuthHeaders(getState),
      });
      return unwrap(response);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchCustomerById = createAsyncThunk(
  "customers/fetchById",
  async ({ customerId }, { getState, rejectWithValue }) => {
    try {
      const response = await axios.get(`${ROOT_URL}/customer/list/${customerId}`, {
        headers: getAuthHeaders(getState),
      });
      return unwrap(response);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateCustomer = createAsyncThunk(
  "customers/update",
  async ({ customerId, body }, { getState, rejectWithValue }) => {
    try {
      const response = await axios.put(
        `${ROOT_URL}/customer/update/${customerId}`,
        withoutCustomerTeamFields(body),
        { headers: getAuthHeaders(getState) }
      );
      return unwrap(response);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createCustomer = createAsyncThunk(
  "customers/create",
  async (body, { getState, rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${ROOT_URL}/customer/create`,
        withoutCustomerTeamFields(body),
        { headers: getAuthHeaders(getState) }
      );
      return unwrap(response);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const banCustomer = createAsyncThunk(
  "customers/ban",
  async ({ customerId, body }, { getState, rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${ROOT_URL}/customer/ban/${customerId}`,
        body,
        { headers: getAuthHeaders(getState) }
      );
      return unwrap(response);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const unbanCustomer = createAsyncThunk(
  "customers/unban",
  async ({ customerId }, { getState, rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${ROOT_URL}/customer/unban/${customerId}`,
        {},
        { headers: getAuthHeaders(getState) }
      );
      return unwrap(response);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteCustomer = createAsyncThunk(
  "customers/delete",
  async ({ customerId }, { getState, rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${ROOT_URL}/customer/delete/${customerId}`,
        {},
        { headers: getAuthHeaders(getState) }
      );
      return unwrap(response);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const bulkCustomers = createAsyncThunk(
  "customers/bulk",
  async (body, { getState, rejectWithValue }) => {
    try {
      const response = await axios.post(`${ROOT_URL}/customer/bulk`, body, {
        headers: getAuthHeaders(getState),
      });
      return unwrap(response);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  list: [],
  listLoading: false,
  listError: null,
  lastFetchedListKey: null,
  cacheByListKey: {},
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
  },
  tabTotals: {},
  detail: null,
  detailLoading: false,
  detailError: null,
  lastFetchedCustomerId: null,
  cacheByCustomerId: {},
  mutationLoading: false,
  mutationError: null,
};

const customersApiSlice = createSlice({
  name: "peopleCustomers",
  initialState,
  reducers: {
    clearCustomerDetail: (state) => {
      state.detail = null;
      state.detailError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCustomersList.pending, (state, action) => {
        if (action.meta?.arg?.countsOnly) return;
        const key = buildCustomersListKey(action.meta?.arg);
        const cached = key && state.cacheByListKey[key];
        if (cached) {
          state.list = cached.list;
          state.pagination = cached.pagination;
          state.listLoading = false;
        } else if (key && key !== state.lastFetchedListKey) {
          state.listLoading = true;
        } else {
          state.listLoading = state.list.length === 0;
        }
        state.listError = null;
      })
      .addCase(fetchCustomersList.fulfilled, (state, action) => {
        const data = action.payload?.data || [];
        const mapped = Array.isArray(data) ? data.map(mapApiCustomerToTableRow) : [];
        const pagination = action.payload?.pagination || {};
        const statusKey = action.meta?.arg?.status || "all";

        if (action.payload?.counts) {
          state.tabTotals = applyApiCustomerCounts(state.tabTotals, action.payload.counts);
        } else {
          state.tabTotals[statusKey] = pagination.total ?? mapped.length;
        }

        if (!action.meta?.arg?.countsOnly) {
          const key = buildCustomersListKey(action.meta?.arg);
          const nextPagination = {
            page: pagination.page ?? 1,
            limit: pagination.limit ?? 10,
            total: pagination.total ?? mapped.length,
            totalPages: pagination.totalPages ?? 1,
            hasNextPage: pagination.hasNextPage ?? false,
            hasPrevPage: pagination.hasPrevPage ?? false,
          };
          state.listLoading = false;
          state.list = mapped;
          state.pagination = nextPagination;
          if (key) {
            state.cacheByListKey[key] = { list: mapped, pagination: nextPagination };
            state.lastFetchedListKey = key;
          }
        }
      })
      .addCase(fetchCustomersList.rejected, (state, action) => {
        if (action.meta?.arg?.countsOnly) return;
        const key = buildCustomersListKey(action.meta?.arg);
        state.listLoading = false;
        state.listError = action.payload;
        if (!key || !state.cacheByListKey[key]) {
          state.list = [];
        }
      })
      .addCase(fetchCustomerById.pending, (state, action) => {
        const idStr =
          action.meta.arg?.customerId != null ? String(action.meta.arg.customerId) : "";
        const cached = idStr && state.cacheByCustomerId[idStr];
        const detailId = state.detail?.id || state.detail?._id;
        const inMemorySame =
          idStr &&
          detailId != null &&
          String(detailId) === idStr &&
          state.lastFetchedCustomerId != null &&
          String(state.lastFetchedCustomerId) === idStr;
        if (cached) {
          state.detail = cached;
          state.detailLoading = false;
        } else if (inMemorySame) {
          state.detailLoading = false;
        } else {
          state.detailLoading = true;
        }
        state.detailError = null;
      })
      .addCase(fetchCustomerById.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.detail = action.payload?.data || null;
        const idStr =
          action.meta.arg?.customerId != null ? String(action.meta.arg.customerId) : "";
        if (idStr && state.detail) {
          state.cacheByCustomerId[idStr] = state.detail;
          state.lastFetchedCustomerId = idStr;
        }
      })
      .addCase(fetchCustomerById.rejected, (state, action) => {
        state.detailLoading = false;
        state.detailError = action.payload;
        const idStr =
          action.meta.arg?.customerId != null ? String(action.meta.arg.customerId) : "";
        if (!idStr || !state.cacheByCustomerId[idStr]) {
          state.detail = null;
        }
      })
      .addMatcher(
        (action) =>
          [
            createCustomer.pending.type,
            updateCustomer.pending.type,
            banCustomer.pending.type,
            unbanCustomer.pending.type,
            deleteCustomer.pending.type,
            bulkCustomers.pending.type,
          ].includes(action.type),
        (state) => {
          state.mutationLoading = true;
          state.mutationError = null;
        }
      )
      .addMatcher(
        (action) =>
          [
            createCustomer.fulfilled.type,
            createCustomer.rejected.type,
            updateCustomer.fulfilled.type,
            updateCustomer.rejected.type,
            banCustomer.fulfilled.type,
            banCustomer.rejected.type,
            unbanCustomer.fulfilled.type,
            unbanCustomer.rejected.type,
            deleteCustomer.fulfilled.type,
            deleteCustomer.rejected.type,
            bulkCustomers.fulfilled.type,
            bulkCustomers.rejected.type,
          ].includes(action.type),
        (state, action) => {
          state.mutationLoading = false;
          if (action.type.endsWith("/rejected")) {
            state.mutationError = action.payload;
          }
        }
      );
  },
});

export const { clearCustomerDetail } = customersApiSlice.actions;
export default customersApiSlice.reducer;
